import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { apiClient } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { accessToken } from '@/lib/staffSession'

/**
 * Le décompte d'un vote — annoncé à la salle, jamais une fermeture.
 *
 * Le MC ouvre « pour deux minutes » : son téléphone, la télé de la salle et la
 * page de vote décomptent ensemble. À zéro, RIEN ne se ferme : on dit que le
 * temps est écoulé, et c'est le MC qui ferme quand la salle a fini.
 *
 * Le serveur ne donne que l'instant d'arrivée (`countdown_ends_at`) et son
 * heure à lui (`server_now`). Chaque écran mesure l'écart entre son horloge et
 * celle du serveur au moment de la lecture, puis décompte seul entre deux
 * lectures : une télé dont l'horloge retarde de quarante secondes affiche le
 * même chiffre que le téléphone du MC.
 */

/** Les durées qu'on annonce au micro. `null` : pas de décompte. */
export const DURATION_PRESETS: (number | null)[] = [null, 60, 120, 180, 300]
export const MAX_DURATION = 3600

/** « 2:05 », « 0:09 » — des minutes non bornées, des secondes sur deux chiffres. */
export function formatClock(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds))
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`
}

/**
 * Secondes restantes, à la seconde près, ou `null` sans décompte.
 *
 * `serverNow` change à chaque lecture : l'écart d'horloge est remesuré à ce
 * moment-là, jamais plus tard — c'est l'instant où il est juste.
 */
export function useCountdown(
  endsAt: string | null | undefined,
  serverNow: string | null | undefined,
): number | null {
  const [offset, setOffset] = useState(() => skew(serverNow))
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    setOffset(skew(serverNow))
    setNow(Date.now())
  }, [serverNow])

  useEffect(() => {
    if (!endsAt) return
    setNow(Date.now())
    // Un quart de seconde : le chiffre change au bon moment, sans attendre
    // jusqu'à une seconde de plus après le vrai passage.
    const timer = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(timer)
  }, [endsAt])

  if (!endsAt) return null
  const end = Date.parse(endsAt)
  if (Number.isNaN(end)) return null
  return Math.max(0, Math.ceil((end - (now + offset)) / 1000))
}

function skew(serverNow: string | null | undefined): number {
  if (!serverNow) return 0
  const server = Date.parse(serverNow)
  return Number.isNaN(server) ? 0 : server - Date.now()
}

// ── Les gestes du MC ───────────────────────────────────────────────

export type CountdownCommand =
  | { kind: 'start'; seconds: number }
  | { kind: 'adjust'; seconds: number }
  | { kind: 'clear' }

/**
 * Lancer, relancer, ajouter, arrêter. Même droit que pour ouvrir ou fermer
 * (`voting.category.control`) ; chaque geste est au journal côté serveur.
 *
 * Les écrans qui montrent le vote se relisent tous : le conducteur, la page
 * d'administration, l'aperçu de l'écran de la salle.
 */
export function useCountdownCommand(eventId: string | undefined, voteId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (command: CountdownCommand) => {
      const options = { token: await accessToken(), ...(eventId ? { eventId } : {}) }
      const path = `/admin/events/${eventId}/vote-categories/${voteId}/countdown`
      switch (command.kind) {
        case 'start':
          return apiClient.put<unknown>(path, { duration_seconds: command.seconds }, options)
        case 'adjust':
          return apiClient.post<unknown>(`${path}/adjust`, { seconds: command.seconds }, options)
        case 'clear':
          return apiClient.delete<unknown>(path, options)
      }
    },
    onSettled: () => {
      for (const key of [
        ['conductor-contest', eventId],
        ['admin-vote-categories', eventId],
        ['conductor-screen', eventId],
        ['public-vote-categories'],
      ]) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })
}

// ── Choisir une durée ──────────────────────────────────────────────

/**
 * « Durée » : les durées qu'on annonce d'un pouce, ou minutes et secondes à
 * la main. « Sans durée » ouvre le vote sans décompte, comme avant.
 */
export function DurationPicker({
  value,
  onChange,
  disabled = false,
  allowNone = true,
}: {
  value: number | null
  onChange: (seconds: number | null) => void
  disabled?: boolean
  /** Faux pour relancer un décompte : « Sans durée » n'y voudrait rien dire. */
  allowNone?: boolean
}) {
  const { t } = useTranslation()
  const id = useId()
  const minutes = value === null ? '' : String(Math.floor(value / 60))
  const seconds = value === null ? '' : String(value % 60).padStart(2, '0')

  const compose = (nextMinutes: string, nextSeconds: string) => {
    const m = Math.max(0, Math.min(60, Number.parseInt(nextMinutes || '0', 10) || 0))
    const s = Math.max(0, Math.min(59, Number.parseInt(nextSeconds || '0', 10) || 0))
    const total = Math.min(MAX_DURATION, m * 60 + s)
    onChange(total > 0 ? total : null)
  }

  return (
    <fieldset className="min-w-0 space-y-2" disabled={disabled}>
      <legend className="text-caption text-content-muted">{t('voteTimer.duration')}</legend>
      <div className="flex flex-wrap gap-2">
        {DURATION_PRESETS.filter((preset) => allowNone || preset !== null).map((preset) => (
          <button
            key={preset ?? 'none'}
            type="button"
            aria-pressed={value === preset}
            onClick={() => onChange(preset)}
            className={cn(
              'min-h-10 rounded-full border px-3 py-1.5 text-sm transition',
              value === preset
                ? 'border-ember-400 bg-ember-400/10 text-content-primary'
                : 'border-line-subtle text-content-secondary hover:text-content-primary',
            )}
          >
            {preset === null ? t('voteTimer.none') : t('voteTimer.preset', { count: preset / 60 })}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor={`${id}-m`} className="sr-only">
          {t('voteTimer.minutesLabel')}
        </label>
        <input
          id={`${id}-m`}
          type="number"
          inputMode="numeric"
          min={0}
          max={60}
          value={minutes}
          placeholder="0"
          onChange={(event) => compose(event.target.value, seconds)}
          className="h-10 w-16 rounded-md border border-line-subtle bg-bg-base px-2 text-center font-mono tabular-nums text-content-primary"
        />
        <span className="text-sm text-content-muted">{t('voteTimer.minutes')}</span>
        <label htmlFor={`${id}-s`} className="sr-only">
          {t('voteTimer.secondsLabel')}
        </label>
        <input
          id={`${id}-s`}
          type="number"
          inputMode="numeric"
          min={0}
          max={59}
          value={seconds}
          placeholder="00"
          onChange={(event) => compose(minutes, event.target.value)}
          className="h-10 w-16 rounded-md border border-line-subtle bg-bg-base px-2 text-center font-mono tabular-nums text-content-primary"
        />
        <span className="text-sm text-content-muted">{t('voteTimer.seconds')}</span>
      </div>
    </fieldset>
  )
}

// ── Le décompte en cours, au conducteur ────────────────────────────

export interface CountdownVote {
  id: string
  status: string
  countdown_seconds: number | null
  countdown_ends_at: string | null
}

/**
 * Pendant le vote : le temps qui reste, en grand, et les gestes qui le
 * règlent. Le vote lui-même (geler, fermer) reste aux boutons habituels —
 * à zéro, ce panneau le rappelle sans rien fermer.
 */
export function CountdownPanel({
  eventId,
  vote,
  remaining,
  canControl,
}: {
  eventId: string | undefined
  vote: CountdownVote
  /** Calculé par l'appelant, qui met aussi en avant « Geler » à zéro. */
  remaining: number | null
  canControl: boolean
}) {
  const { t } = useTranslation()
  const command = useCountdownCommand(eventId, vote.id)
  const [choosing, setChoosing] = useState(false)
  const [duration, setDuration] = useState<number | null>(vote.countdown_seconds ?? 120)

  if (vote.status !== 'open') return null
  const send = (next: CountdownCommand, after?: () => void) =>
    command.mutate(next, after ? { onSuccess: after } : undefined)

  return (
    <div className="mt-3 space-y-3 rounded-md bg-bg-overlay/60 px-3 py-3">
      {remaining !== null ? (
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
          <div className="min-w-0">
            <p className="text-caption text-content-muted">{t('voteTimer.remaining')}</p>
            <p
              role="timer"
              aria-live="off"
              className={cn(
                'font-mono text-[2.75rem] leading-none tabular-nums',
                remaining === 0
                  ? 'text-state-warning'
                  : remaining <= 10
                    ? 'text-ember-300 motion-safe:animate-pulse'
                    : 'text-content-primary',
              )}
            >
              {formatClock(remaining)}
            </p>
          </div>
          {vote.countdown_seconds ? (
            <p className="text-caption text-content-muted">
              {t('voteTimer.of', { time: formatClock(vote.countdown_seconds) })}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-caption text-content-muted">{t('voteTimer.noCountdown')}</p>
      )}

      {remaining === 0 ? (
        <p role="status" className="text-sm font-medium text-state-warning">
          {t('voteTimer.elapsed')}
        </p>
      ) : null}

      {canControl && remaining !== null && !choosing ? (
        // Deux par ligne sur un téléphone : le pouce vise sans viser.
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Button
            size="sm"
            variant="outline"
            disabled={command.isPending}
            onClick={() => send({ kind: 'adjust', seconds: 30 })}
          >
            {t('voteTimer.add30')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={command.isPending}
            onClick={() => send({ kind: 'adjust', seconds: 60 })}
          >
            {t('voteTimer.add60')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={command.isPending}
            onClick={() => setChoosing(true)}
          >
            {t('voteTimer.restart')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={command.isPending}
            onClick={() => send({ kind: 'clear' })}
          >
            <span className="min-w-0 whitespace-normal text-center leading-tight">
              {t('voteTimer.stop')}
            </span>
          </Button>
        </div>
      ) : null}

      {/* Relancer, ou lancer un décompte après avoir ouvert sans durée :
          on choisit la durée, puis on confirme — rien ne part au premier
          appui sur une pastille. */}
      {canControl && (remaining === null || choosing) ? (
        <div className="space-y-3">
          <DurationPicker
            value={duration}
            onChange={setDuration}
            disabled={command.isPending}
            allowNone={false}
          />
          <div className="grid grid-cols-2 gap-2">
            {/* Seul, il prend la ligne : dans la carte d'une étape, une
                demi-ligne ne loge pas « Lancer le décompte ». */}
            <Button
              size="sm"
              variant="outline"
              className={cn('min-w-0', !choosing && 'col-span-2')}
              disabled={command.isPending || duration === null}
              onClick={() =>
                duration !== null &&
                send({ kind: 'start', seconds: duration }, () => setChoosing(false))
              }
            >
              <span className="min-w-0 whitespace-normal text-center leading-tight">
                {remaining === null ? t('voteTimer.start') : t('voteTimer.restart')}
              </span>
            </Button>
            {choosing ? (
              <Button size="sm" variant="ghost" onClick={() => setChoosing(false)}>
                {t('admin.cancel')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {command.isError ? (
        <p className="text-caption text-state-danger">
          {command.error instanceof Error ? command.error.message : t('state.errorTitle')}
        </p>
      ) : null}
    </div>
  )
}
